import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'
import * as React from 'react'

import PrivateSessionDetail from './PrivateSessionDetail'
import { currentUserAtom } from '../../domains/user/AtomUsers'
import PrivateSessionService from '../../services/PrivateSessionService'
import { STATUS } from '../../share/Constant'
import { isAdmin, isOwner } from '../../share/utility'
import { VIEW_TYPES } from '../../utils/utility'
import useBar from '../common/Bar'
import EventDisplay from '../common/EventDisplay'
import ConfirmationDialog from '../dialog/ConfirmationDialog'
import PrivateSessionForm from '../forms/PrivateSessionForm'

/**
 * PrivateSessionCard component that can be used in both student and instructor views
 * @param {Object} props
 * @param {Object} props.session - The session object containing all details
 * @param {string} props.viewer - Viewer mode: 'student' or 'instructor'
 * @param {function} props.onUpdate - Callback function when session is updated
 * @param {function} props.onDelete - Callback function when session is deleted
 * @param {Object} props.sx - Additional styles to apply to the card
 */
export default function PrivateSessionCard({ session, viewer = VIEW_TYPES.USER, updateHook, deleteHook, sx = {} }) {
  const user = useAtomValue(currentUserAtom)

  const bar = useBar()

  const isInstructorView = viewer === VIEW_TYPES.INSTRUCTOR

  const [unregisterDialogOpen, setUnregisterDialogOpen] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [detailDialogOpen, setDetailDialogOpen] = React.useState(false)

  const [submitting, setSubmitting] = React.useState(false)
  const [editFormData, setEditFormData] = React.useState(null)
  const [editFormErrors, setEditFormErrors] = React.useState({})

  // Transform session data for EventDisplay
  const sessionData = React.useMemo(() => {
    if (!session) return null
    return {
      resourceId: session.resourceId,
      title: 'Private Session',
      description: session.description,
      skiResort: session.skiResort || session.location,
      sport: session.sport || session.sportType,
      date: session.date,
      timeSlot: session.timeSlot,
      status: session.status,
      instructorId: !isInstructorView ? session.instructorId : null,
      participantIds: isInstructorView ? session.participantIds : null,
      showSpotsTaken: false,
      participantsDetail: isInstructorView && (isOwner(user, session) || isAdmin(user)),
    }
  }, [session, user, isInstructorView])

  const viewAction = {
    dialogOpen: () => setDetailDialogOpen(true),
    dialogClose: () => setDetailDialogOpen(false),
  }

  const deleteAction = {
    dialogOpen: () => setDeleteDialogOpen(true),
    dialogClose: () => setDeleteDialogOpen(false),
    do: async () => {
      try {
        await PrivateSessionService.deletePrivateSession(session.resourceId)
        bar.success('Session deleted successfully')
        setDeleteDialogOpen(false)
        deleteHook?.()
      } catch (error) {
        console.error('Error deleting session:', error)
        bar.error(error.message || 'Failed to delete session')
      }
    },
  }

  const editAction = {
    dialogOpen: () => {
      setEditFormData({
        sport: session.sport,
        skiResort: session.skiResort,
        price: session.price,
        description: session.description,
      })
      setEditDialogOpen(true)
    },
    dialogClose: () => {
      setEditDialogOpen(false)
      setEditFormData(null)
      setEditFormErrors({})
    },
    do: async () => {
      setSubmitting(true)

      try {
        // Only include fields that are defined in the OpenAPI spec for PrivateSession
        const updatedSessionData = {
          skiResort: editFormData.skiResort,
          sport: editFormData.sport,
          description: editFormData.description,
          price: Number(editFormData.price),
          // TODO: remove these fields
          resourceId: session.resourceId,
          startTime: session.startTime,
          endTime: session.endTime,
          instructorId: session.instructorId,
          participantIds: session.participantIds || [],
          status: session.status,
        }

        await PrivateSessionService.updatePrivateSession(session.resourceId, updatedSessionData)

        bar.success('Session updated successfully')
        updateHook?.()
        setEditDialogOpen(false)
      } catch (error) {
        console.error('Error updating session:', error)
        bar.error(error.message || 'Failed to update session')
      } finally {
        setSubmitting(false)
      }
    },
  }

  const unregisterAction = {
    dialogOpen: async () => {
      const editable = isAdmin(user) || isOwner(user, session)

      if (editable) {
        setUnregisterDialogOpen(true)
        return
      }

      if (session.cancellationDeadline) {
        const now = dayjs()
        const cancellationDeadline = dayjs(session.cancellationDeadline)
        if (now.isAfter(cancellationDeadline)) {
          bar.error('Cancellation deadline has passed. If you have an emergency, please contact the instructor directly.')
          return
        }
      }

      setUnregisterDialogOpen(true)
    },
    dialogClose: () => {
      setUnregisterDialogOpen(false)
    },
    do: async (selectedParticipants) => {
      if (selectedParticipants.length === 0) {
        bar.warn('Please select participants to unregister')
        return
      }

      try {
        await PrivateSessionService.cancelSession(session.resourceId)
        bar.success('Successfully unregistered participants')
        setUnregisterDialogOpen(false)
        updateHook?.()
      } catch (err) {
        console.error('Failed to unregister participants:', err)
        bar.error(err.message || 'Failed to unregister participants')
      }
    },
  }

  // Define actions based on mode and session status
  const actions = React.useMemo(() => {
    const isCompleted = session?.status === STATUS.COMPLETED
    const isBooked = session?.status === STATUS.BOOKED

    let actions = {
      enabled: true,
      viewable: false,
      unregisterable: isBooked && !isCompleted,
      deleteable: isInstructorView && !isCompleted,
      onUnregister: unregisterAction.dialogOpen,
      onDelete: deleteAction.dialogOpen,

      editable: false, // We don't allow instructors to edit single session
      // editable: isInstructorView && !isCompleted,
      // onEdit: editAction.dialogOpen,

      onView: viewAction.dialogOpen,
    }

    actions.enabled = actions.editable || actions.unregisterable || actions.deleteable || actions.viewable
    return actions
  }, [session?.status])

  if (!sessionData) return null

  return (
    <>
      <EventDisplay user={user} event={sessionData} mode="small" actions={actions} sx={sx} />

      {/* Detail Dialog */}
      {detailDialogOpen && (
        <ConfirmationDialog
          open={detailDialogOpen}
          title="Session Detail"
          content=""
          actions={{
            cancel: { onClick: viewAction.dialogClose },
            confirm: { label: 'Close', onClick: viewAction.dialogClose },
          }}
          maxWidth="md"
        >
          <PrivateSessionDetail booking={session} viewer={viewer} />
        </ConfirmationDialog>
      )}

      {/* Edit Dialog */}
      {editDialogOpen && (
        <ConfirmationDialog
          open={editDialogOpen}
          maxWidth="md"
          title="Edit Private Session"
          content=""
          actions={{
            cancel: { label: 'Cancel', onClick: editAction.dialogClose },
            confirm: { label: 'Save Changes', onClick: editAction.do, color: 'info', variant: 'contained', submitting: submitting },
          }}
        >
          <PrivateSessionForm formData={editFormData} formErrors={editFormErrors} onFormChange={setEditFormData} isEdit={true} />
        </ConfirmationDialog>
      )}

      {/* Unregister Dialog */}
      {unregisterDialogOpen && (
        <ConfirmationDialog
          open={unregisterDialogOpen}
          title="Confirm Unregister"
          content="Are you sure you want to unregister from this session? This action cannot be undone."
          actions={{
            cancel: {
              label: 'Cancel',
              onClick: unregisterAction.dialogClose,
            },
            confirm: {
              label: 'Confirm Unregister',
              onClick: unregisterAction.do,
              color: 'error',
              variant: 'contained',
            },
          }}
        />
      )}

      {/* Delete Dialog */}
      {deleteDialogOpen && (
        <ConfirmationDialog
          open={deleteDialogOpen}
          title="Confirm Delete"
          content="Are you sure you want to delete this session? This action cannot be undone."
          actions={{
            cancel: { label: 'Cancel', onClick: deleteAction.dialogClose },
            confirm: { label: 'Confirm', onClick: deleteAction.do, color: 'error', variant: 'contained' },
          }}
        />
      )}
    </>
  )
}
